import { useMemo, useState } from 'react';
import { Alert, Button, GlobalStyles, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ApplicationJourneyHeaderDto } from '../../api/admin';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import {
  mapLoanReviewToViewModel,
  type ReviewReasonNote,
  type IncomeSubTarget,
} from '../../lib/loanReview/mapLoanReviewToViewModel';
import { LoanReviewHero } from './LoanReviewHero';
import { LoanReviewSourceCards } from './LoanReviewSourceCards';
import { LoanReviewConsistencyMatrix } from './LoanReviewConsistencyMatrix';
import { LoanReviewSections } from './LoanReviewSections';
import { FLASH_CLASS } from './loanReviewAnchors';

type Props = {
  applicationId: string;
  applicationStatus?: string;
  completedMilestones?: string[];
  loanReview?: ApplicationJourneyHeaderDto['loanReview'];
  /**
   * Parent switches application detail tabs (keeps UI + ?tab= in sync).
   * `incomeSubTarget` opens a specific Income sub-view (e.g. the Gambling
   * debits filter) instead of leaving the reviewer to find it themselves.
   */
  onNavigateToTab?: (tab: ApplicationTabKey, incomeSubTarget?: IncomeSubTarget | null) => void;
};

export function ApplicationLoanReviewTab({
  applicationId: _applicationId,
  applicationStatus,
  completedMilestones: _completedMilestones,
  loanReview,
  onNavigateToTab,
}: Props) {
  const vm = useMemo(() => mapLoanReviewToViewModel(loanReview), [loanReview]);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [highlightField, setHighlightField] = useState<string | null>(null);
  /** Bumps on every jump so re-clicking the same link scrolls again. */
  const [jumpNonce, setJumpNonce] = useState(0);

  const jumpToCrossCheck = (targetField?: string | null) => {
    setMatrixOpen(true);
    setHighlightField(targetField ?? null);
    setJumpNonce((n) => n + 1);
  };

  const jumpToAppTab = (tab: 'employer' | 'identity' | 'income', incomeSubTarget?: IncomeSubTarget | null) => {
    if (onNavigateToTab) {
      onNavigateToTab(tab, incomeSubTarget);
      return;
    }
  };

  const jumpNote = (note: ReviewReasonNote) => {
    const text = note.text || '';
    const tab: 'employer' | 'identity' | 'income' | null =
      note.appTab ??
      (/see\s+employer\s+tab|employer exists|trustworthy workplace|work setup|consistent with the employer/i.test(
        text,
      )
        ? 'employer'
        : /see\s+income\s+tab/i.test(text)
          ? 'income'
          : /see\s+identity\s+tab/i.test(text)
            ? 'identity'
            : null);
    if (tab) {
      jumpToAppTab(tab, note.incomeSubTarget);
      return;
    }
    jumpToCrossCheck(note.targetField);
  };

  if (!loanReview?.requestStatus) {
    return (
      <Alert severity="info" data-testid="application-loan-review-tab-empty">
        <Typography
          variant="caption"
          sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}
        >
          What&apos;s happening
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.35 }}>
          Loan review has not started yet.
        </Typography>
        <Typography variant="body2">
          This step usually runs after employment and personal details are in place.
        </Typography>
      </Alert>
    );
  }

  return (
    <Stack spacing={2} data-testid="application-loan-review-tab">
      {/* Jump-target highlight for the combined-result chips. Defined once here
          rather than per-target: the chips can send you to a source card or to the
          Agent 3 panel, and both should acknowledge the arrival the same way.
          `scrollMarginTop` keeps a target clear of the sticky page header. */}
      <GlobalStyles
        styles={(t) => ({
          '[id^="loan-review-anchor-"]': { scrollMarginTop: 96 },
          '@keyframes loanReviewJumpFlash': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(t.palette.primary.main, 0.5)}` },
            '35%': { boxShadow: `0 0 0 5px ${alpha(t.palette.primary.main, 0.28)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(t.palette.primary.main, 0)}` },
          },
          [`.${FLASH_CLASS}`]: {
            animation: 'loanReviewJumpFlash 1.4s ease-out',
            borderRadius: t.shape.borderRadius,
          },
          // Someone who has asked for less motion still needs to see where they
          // landed — hold a static ring instead of pulsing one.
          '@media (prefers-reduced-motion: reduce)': {
            [`.${FLASH_CLASS}`]: {
              animation: 'none',
              boxShadow: `0 0 0 3px ${alpha(t.palette.primary.main, 0.35)}`,
            },
          },
        })}
      />
      <LoanReviewHero
        vm={vm}
        applicationStatus={applicationStatus}
        manualReviewHold={loanReview?.manualReviewHold ?? null}
        onJumpToCrossCheck={jumpToCrossCheck}
        onJumpNote={jumpNote}
        onJumpToAppTab={jumpToAppTab}
      />
      <LoanReviewSourceCards vm={vm} />
      <LoanReviewConsistencyMatrix
        rows={vm.crossChecks}
        open={matrixOpen}
        onOpenChange={setMatrixOpen}
        highlightField={highlightField}
        jumpNonce={jumpNonce}
      />
      <LoanReviewSections vm={vm} />
      <Stack spacing={0.5} sx={{ pt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Related verification tabs
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="text" onClick={() => jumpToAppTab('identity')}>
            Identity
          </Button>
          <Button size="small" variant="text" onClick={() => jumpToAppTab('income')}>
            Income
          </Button>
          <Button size="small" variant="text" onClick={() => jumpToAppTab('employer')}>
            Employer
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
